<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { Database, Search, Globe, Plug, Lock, Cpu } from '@lucide/svelte';

  const dataConnectors = [
    { label: 'Azure AI Search', description: 'Enterprise knowledge retrieval', icon: Search },
    { label: 'Database & file system connectors', description: 'SQL, NoSQL, blob storage, file shares', icon: Database },
    { label: 'Private network access', description: 'Connect to systems behind your firewall', icon: Lock },
  ];

  const extensionPoints = [
    { label: 'MCP (Model Context Protocol)', description: 'Standard protocol for tool integration', icon: Plug },
    { label: 'OpenAPI', description: 'Connect any API with an OpenAPI spec', icon: Globe },
    { label: 'Model-agnostic design', description: 'OpenAI, Anthropic, Azure, open source — swap providers as the landscape evolves', icon: Cpu },
  ];
</script>

<PageMeta
  title="Connectors & Integrations — Wire AI into your systems"
  description="Secure, governed connections to your databases, documents, and internal tools. MCP, OpenAPI, Azure AI Search — behind your firewall, under your control."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Wire AI into the systems where your business runs.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          AI without context is a parlor trick. The value comes when AI is connected to your databases, your documents, your internal tools, your APIs — the actual systems where your business operates. That's what makes it yours.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: Governed connections -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-6">
          Secure. Governed. Auditable.
        </h2>
        <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
          Connect to data sources behind private networks without exposing them to the public internet. Every connection respects your existing access controls. Every query is logged. Every result is scoped to the permissions of the user or agent requesting it.
        </p>
      </div>

      <!-- Two-column grouped -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Data connectors -->
        <div>
          <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-6 block">Data connectors</span>
          <div class="flex flex-col gap-0">
            {#each dataConnectors as conn, i}
              <div class="flex items-start gap-4 py-5 {i < dataConnectors.length - 1 ? 'border-b border-[#333335]' : ''}">
                <CornerFrame
                  class="w-[36px] h-[36px] flex items-center justify-center shrink-0"
                  bg="bg-transparent"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  cornerColor="white"
                  cornerSize={6}
                >
                  <svelte:component this={conn.icon} class="w-4 h-4 text-white" />
                </CornerFrame>
                <div>
                  <span class="font-sans text-[15px] text-white">{conn.label}</span>
                  <p class="font-mono font-light text-[13px] text-[#878787] mt-1">{conn.description}</p>
                </div>
              </div>
            {/each}
          </div>
        </div>

        <!-- Extension points -->
        <div>
          <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-6 block">Extension points</span>
          <div class="flex flex-col gap-0">
            {#each extensionPoints as ext, i}
              <div class="flex items-start gap-4 py-5 {i < extensionPoints.length - 1 ? 'border-b border-[#333335]' : ''}">
                <CornerFrame
                  class="w-[36px] h-[36px] flex items-center justify-center shrink-0"
                  bg="bg-transparent"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  cornerColor="white"
                  cornerSize={6}
                >
                  <svelte:component this={ext.icon} class="w-4 h-4 text-white" />
                </CornerFrame>
                <div>
                  <span class="font-sans text-[15px] text-white">{ext.label}</span>
                  <p class="font-mono font-light text-[13px] text-[#878787] mt-1">{ext.description}</p>
                </div>
              </div>
            {/each}
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §2: Private network callout -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <div class="border-l-2 border-[#ee6018] pl-6 py-2 mb-8">
          <p class="font-mono font-light text-[18px] text-[#020202] leading-relaxed">
            Corral makes those connections secure, governed, and auditable. Connect to systems behind private networks without exposing them to the public internet.
          </p>
        </div>
        <p class="font-body font-light text-[16px] text-[#5C5855] leading-relaxed">
          Azure Cognitive Services — vision, language, speech, decision. OpenAI and Anthropic model access via your own deployments. Swap providers as the landscape evolves without rearchitecting.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
